import { TimerIcon } from "@phosphor-icons/react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { PhosphorIcon } from "../phosphorIcon/PhosphorIcon";
import useBreakpoints, { Breakpoint } from "../../hooks/useBreakpoints";
import PhaseSixIcon from "../phaseSixIcon/PhaseSixIcon";

interface TimeOption {
    label: string;
    value: number;
}

const HOURS_OPTIONS: TimeOption[] = [
    { label: "0 (h)", value: 0 },
    { label: "1 (h)", value: 60 },
    { label: "2 (h)", value: 120 },
    { label: "3 (h)", value: 180 },
    { label: "4 (h)", value: 240 },
    { label: "5 (h)", value: 300 },
];

const MINUTES_OPTIONS: TimeOption[] = [
    { label: "00 (min)", value: 0 },
    { label: "05 (min)", value: 5 },
    { label: "10 (min)", value: 10 },
    { label: "15 (min)", value: 15 },
    { label: "20 (min)", value: 20 },
    { label: "25 (min)", value: 25 },
    { label: "30 (min)", value: 30 },
    { label: "35 (min)", value: 35 },
    { label: "40 (min)", value: 40 },
    { label: "45 (min)", value: 45 },
    { label: "50 (min)", value: 50 },
    { label: "55 (min)", value: 55 },
];

interface DurationInputProps {
    hours: string;
    minutes: string;
    hoursPlaceholder: string;
    minutesPlaceholder: string;
    onHoursSelect: (value: number) => void;
    onMinutesSelect: (value: number) => void;
}

type ActiveDropdown = "hours" | "minutes" | null;

const DurationInput = (props: DurationInputProps) => {
    const { getStyleBasedOnBreakpoint } = useBreakpoints();

    const [activeDropdown, setActiveDropdown] = useState<ActiveDropdown>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

    const hoursRef = useRef<HTMLDivElement>(null);
    const minutesRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const getActiveRef = () => (activeDropdown === "hours" ? hoursRef : minutesRef);

    const calculateDropdownPosition = (ref: React.RefObject<HTMLDivElement | null>) => {
        if (!ref.current) return { top: 0, left: 0, width: 0 };
        const rect = ref.current.getBoundingClientRect();
        const dropdownHeight = 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const openUp = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;

        return {
            top: openUp ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
        };
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            const activeRef = getActiveRef();
            const isInside = activeRef.current?.contains(target) || dropdownRef.current?.contains(target);
            if (!isInside) {
                setActiveDropdown(null);
            }
        };

        const handleEscapeKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") setActiveDropdown(null);
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscapeKey);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscapeKey);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDropdown]);

    useEffect(() => {
        if (!activeDropdown) return;

        const handleResizeOrScroll = () => {
            setDropdownPosition(calculateDropdownPosition(getActiveRef()));
        };

        window.addEventListener("resize", handleResizeOrScroll);
        window.addEventListener("scroll", handleResizeOrScroll, true);

        return () => {
            window.removeEventListener("resize", handleResizeOrScroll);
            window.removeEventListener("scroll", handleResizeOrScroll, true);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeDropdown]);

    const handleDropdownToggle = (type: "hours" | "minutes") => {
        if (activeDropdown === type) {
            setActiveDropdown(null);
        } else {
            const ref = type === "hours" ? hoursRef : minutesRef;
            setDropdownPosition(calculateDropdownPosition(ref));
            setActiveDropdown(type);
        }
    };

    const handleSelect = (option: TimeOption) => {
        if (activeDropdown === "hours") {
            props.onHoursSelect(option.value);
        } else {
            props.onMinutesSelect(option.value);
        }
        setActiveDropdown(null);
    };

    const renderDropdown = (options: TimeOption[]) => {
        return createPortal(
            <div
                ref={dropdownRef}
                className="fixed z-[1001] bg-p6-grey-light border-2 border-p6-grey-lightest rounded-[15px] overflow-y-auto flex flex-col gap-px max-h-[200px]"
                style={dropdownPosition}
            >
                {options.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        onClick={() => handleSelect(option)}
                        className="bg-white h-[40px] shrink-0 flex items-center justify-center pl-4 pr-8 py-2 cursor-pointer hover:bg-background-light text-m text-p6-grey-dark text-center"
                    >
                        {option.label}
                    </button>
                ))}
            </div>,
            document.body
        );
    };

    return (
        <div
            className={`flex items-stretch w-full border border-p6-grey-dark rounded-small-container min-h-[50px] ${getStyleBasedOnBreakpoint(
                Breakpoint.MD,
                "flex-row",
                "flex-col"
            )}`}
        >
            <div
                className={`items-center justify-center px-s border-r border-p6-grey-mid ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "flex",
                    "hidden"
                )}`}
            >
                <PhosphorIcon
                    phosphorIcon={TimerIcon}
                    color="text-p6-grey-dark"
                    fontSize="text-xl"
                />
            </div>

            <div
                ref={hoursRef}
                className={`relative flex flex-1 min-h-[50px] ${getStyleBasedOnBreakpoint(
                    Breakpoint.MD,
                    "border-r border-p6-grey-mid",
                    "border-b border-p6-grey-mid"
                )}`}
            >
                <button
                    type="button"
                    onClick={() => handleDropdownToggle("hours")}
                    aria-label={props.hoursPlaceholder}
                    aria-expanded={activeDropdown === "hours"}
                    className={`flex flex-1 items-center justify-center gap-s px-xs cursor-pointer hover:bg-background-light ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "",
                        "rounded-t-small-container"
                    )}`}
                >
                    <span className="text-m text-p6-grey-dark">{props.hours || props.hoursPlaceholder}</span>
                    <PhaseSixIcon
                        name="chevron-down"
                        className="text-[20px] text-p6-grey-dark"
                    />
                </button>
                {activeDropdown === "hours" && renderDropdown(HOURS_OPTIONS)}
            </div>

            <div
                ref={minutesRef}
                className="relative flex flex-1 min-h-[50px]"
            >
                <button
                    type="button"
                    onClick={() => handleDropdownToggle("minutes")}
                    aria-label={props.minutesPlaceholder}
                    aria-expanded={activeDropdown === "minutes"}
                    className={`flex flex-1 items-center justify-center gap-s px-xs cursor-pointer hover:bg-background-light ${getStyleBasedOnBreakpoint(
                        Breakpoint.MD,
                        "rounded-r-small-container",
                        "rounded-b-small-container"
                    )}`}
                >
                    <span className="text-m text-p6-grey-dark">{props.minutes || props.minutesPlaceholder}</span>
                    <PhaseSixIcon
                        name="chevron-down"
                        className="text-[20px] text-p6-grey-dark"
                    />
                </button>
                {activeDropdown === "minutes" && renderDropdown(MINUTES_OPTIONS)}
            </div>
        </div>
    );
};

export default DurationInput;
